import { valueOf } from './axes.js';

function draw(chart, ctx, progress) {
  const s = chart.options.series[0];
  if (!s) return [];

  const showLabels = chart.options.dataLabels !== false; // default true, like CanvasJS indexLabel
  const { x, y, w, h } = chart._plot;
  const cx = x + w / 2;
  const cy = y + h / 2;
  // Leave room around the circle for leader lines + text when labels are on.
  const maxR = Math.min(w, h) / 2 - (showLabels ? Math.min(w, h) * 0.22 : 10);
  const outerR = Math.max(maxR, 20);
  const innerR = chart.options.type === 'doughnut' ? outerR * 0.55 : 0;
  const explodeDist = outerR * 0.12;

  const total = s.data.reduce((sum, d) => sum + valueOf(d), 0) || 1;
  let angle = -Math.PI / 2;
  const hitAreas = [];
  const labelQueue = []; // drawn after all slices so leader lines never sit under a later slice

  s.data.forEach((d, i) => {
    const value = valueOf(d);
    const label = typeof d === 'object' ? d.x ?? d.label ?? `Slice ${i + 1}` : `Slice ${i + 1}`;
    const color = (d.color) || chart.options.colors[i % chart.options.colors.length];
    const sweep = (value / total) * Math.PI * 2 * progress;
    const start = angle;
    const end = angle + sweep;
    const midAngle = (start + end) / 2;

    // Click-to-explode: offset this slice's own center outward along its mid-angle.
    const isExploded = i === chart._explodeIndex;
    const explodeAmt = isExploded ? (chart._explodeAmount || 0) : 0;
    const sliceCx = cx + explodeDist * explodeAmt * Math.cos(midAngle);
    const sliceCy = cy + explodeDist * explodeAmt * Math.sin(midAngle);

    ctx.beginPath();
    ctx.moveTo(sliceCx + innerR * Math.cos(start), sliceCy + innerR * Math.sin(start));
    ctx.lineTo(sliceCx + outerR * Math.cos(start), sliceCy + outerR * Math.sin(start));
    ctx.arc(sliceCx, sliceCy, outerR, start, end);
    ctx.lineTo(sliceCx + innerR * Math.cos(end), sliceCy + innerR * Math.sin(end));
    if (innerR > 0) ctx.arc(sliceCx, sliceCy, innerR, end, start, true);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.stroke();

    if (showLabels && progress >= 1 && sweep > 0.001) {
      labelQueue.push({ midAngle, label, value, sliceCx, sliceCy });
    } else if (!showLabels && progress >= 1 && sweep > 0.15) {
      const labelR = (outerR + innerR) / 2;
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const pct = Math.round((value / total) * 100);
      ctx.fillText(`${pct}%`, sliceCx + labelR * Math.cos(midAngle), sliceCy + labelR * Math.sin(midAngle));
    }

    const hitR = (outerR + innerR) / 2;
    hitAreas.push({
      contains: (mx, my) => {
        const dx = mx - sliceCx;
        const dy = my - sliceCy;
        const dist = Math.hypot(dx, dy);
        if (dist < innerR || dist > outerR) return false;
        let a = Math.atan2(dy, dx);
        let s0 = start;
        let e0 = end;
        while (a < s0) a += Math.PI * 2;
        return a >= s0 && a <= e0;
      },
      tooltipX: sliceCx + hitR * Math.cos(midAngle),
      tooltipY: sliceCy + hitR * Math.sin(midAngle),
      label: `<b>${label}</b><br>${value} (${Math.round((value / total) * 100)}%)`,
      raw: { seriesIndex: 0, seriesName: s.name, dataIndex: i, x: label, y: value, percent: value / total },
    });

    angle = end;
  });

  if (showLabels && labelQueue.length) {
    const elbowOut = 14;
    const elbowFlat = 16;
    ctx.font = '12px Arial';
    ctx.strokeStyle = '#999';
    ctx.lineWidth = 1;
    ctx.fillStyle = '#333';
    ctx.textBaseline = 'middle';

    labelQueue.forEach(({ midAngle, label, value, sliceCx, sliceCy }) => {
      const text = `${label}: ${value}`;
      const onCircle = { x: sliceCx + outerR * Math.cos(midAngle), y: sliceCy + outerR * Math.sin(midAngle) };
      const elbow = { x: sliceCx + (outerR + elbowOut) * Math.cos(midAngle), y: sliceCy + (outerR + elbowOut) * Math.sin(midAngle) };
      const goRight = Math.cos(midAngle) >= 0;
      const flat = { x: elbow.x + (goRight ? elbowFlat : -elbowFlat), y: elbow.y };

      ctx.beginPath();
      ctx.moveTo(onCircle.x, onCircle.y);
      ctx.lineTo(elbow.x, elbow.y);
      ctx.lineTo(flat.x, flat.y);
      ctx.stroke();

      ctx.textAlign = goRight ? 'left' : 'right';
      ctx.fillText(text, flat.x + (goRight ? 4 : -4), flat.y);
    });
  }

  return hitAreas;
}

const PieChart = { draw };
export default PieChart;
